import { generateUserAgentValues } from '@financial/app-js-interfaces';

/**
 * 네이버 인증서를 검증하는 환경에 대한 비트마스킹 인터페이스 (담당자: 김남훈님)
 * NOTE: 여러 비트를 조합해서 요청해도, 순차 검증에 따라 하나의 코드만 돌려주기 때문에 다수의 실패 사유를 한 번에 파악하는 것은 불가능하다.
 * @see https://pages.oss.navercorp.com/id-mobilesdk/nid-sdk-docs/docs/sign/interfaces/환경점검인터페이스
 * */
const verifyNaverSignInstalledEnv = function () {
  let checkingCode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 32;
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      throw new Error('verifyNaverSignInstalledEnv 는 window 환경에서만 실행할 수 있습니다.');
    }
    const {
      NaverSign
    } = window;
    const {
      isAndroid,
      isIOS,
      checkAvailableNaverApp
    } = generateUserAgentValues(window.navigator.userAgent);

    // 버전별 인터페이스 호출 기준 https://oss.fin.navercorp.com/techFin/myasset-plan/issues/1233#issuecomment-511150
    const checkNaverSignAvailable = (() => {
      if (isAndroid) {
        if (checkAvailableNaverApp('11.7.0')) {
          return NaverSign?.isNaverSignAvailable;
        }
      }
      if (isIOS) {
        if (checkAvailableNaverApp('11.16.0')) {
          return NaverSign?.asyncNaverSignAvailable;
        }
        if (checkAvailableNaverApp('11.7.0')) {
          return NaverSign?.isNaverSignAvailable;
        }
      }
    })();
    if (!checkNaverSignAvailable) {
      reject(new Error('해당 인터페이스를 제공하지 않는 환경입니다.'));
      return;
    }
    const timestamp = new Date().getTime();
    const callbackName = `callbackFor_checkNaverSignAvailable_${timestamp}`;

    /*
     * 해당 API의 스펙 상, 콜백함수가 전역에 위치해야 하기 때문에 일회성 콜백을 등록한다.
     * code는 Number이다.
     * */
    window[callbackName] = _ref => {
      let {
        code
      } = _ref;
      resolve(code);
      delete window[callbackName];
    };
    const appCheckObj = {
      code: checkingCode,
      callbackFunc: callbackName
    };
    checkNaverSignAvailable.call(NaverSign, JSON.stringify(appCheckObj));
  });
};
/**
 * 현재 스마트폰에 원격 제어앱이 설치되어있는지 탐지합니다.
 * @see https://pages.oss.navercorp.com/id-mobilesdk/nid-sdk-docs/docs/sign/android/네이버페이%20보안%20관련%20인터페이스/#원격제어-앱-탐지
 */
const scanRemoteApps = () => new Promise((resolve, reject) => {
  if (typeof window.NaverSign?.scanRemoteApps === 'undefined') {
    reject(new Error('해당 인터페이스를 제공하지 않는 환경입니다.'));
    return;
  }
  function eventListener(event) {
    resolve(event.detail);
    window.removeEventListener('scan_remoteapps', eventListener);
  }
  window.addEventListener('scan_remoteapps', eventListener);
  window.NaverSign.scanRemoteApps();
});

/**
 * 스마트폰에 악성 앱이 설치되어있는지 탐지합니다.
 * 악성 앱 탐지시 네이티브 화면이 노출됩니다.
 * @see https://pages.oss.navercorp.com/id-mobilesdk/nid-sdk-docs/docs/sign/android/네이버페이%20보안%20관련%20인터페이스/#악성-앱-탐지
 */
const scanMalwareApps = () => {
  if (typeof window.NaverSign?.scanMalware === 'undefined') {
    return;
  }
  window.NaverSign.scanMalware();
};

export { scanMalwareApps, scanRemoteApps, verifyNaverSignInstalledEnv };
